import React, {
  useEffect,
  useState,
} from "react";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import QRCode from "qrcode";

import SchoolLogo from "./passionaAidsLogo.jpg";
import AvatarStudent from "./studentavv.png";

/* =========================================================
   Passion Aids The Future
   ONE A4 PAGE
   TWO RECEIPT COPIES
========================================================= */

const COLORS = {
  green: "#087443",
  greenDark: "#064C2D",
  greenLight: "#EAF5EF",

  gold: "#F2C94C",
  goldDark: "#A77B08",
  goldLight: "#FFF8DD",

  black: "#111111",
  text: "#202020",
  muted: "#5F6368",

  white: "#FFFFFF",
  border: "#D5DDD8",

  red: "#B42318",
};

const CENTER_NAME = "Passion Aids The Future";

const CENTER_SUBTITLE =
  "TRAINING • SKILLS DEVELOPMENT • WORKFORCE READINESS";

/* =========================================================
   HELPERS
========================================================= */

const toNumber = (value, fallback = 0) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

const formatMoney = (value) => {
  return toNumber(value).toLocaleString(
    "en-US",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

const getPaymentCurrency = (record) => {
  const currency = String(
    record?.payment_currency ||
      record?.currency ||
      "LRD"
  ).toUpperCase();

  return currency === "USD"
    ? "USD"
    : "LRD";
};

const getCurrencyLabel = (currency) => {
  return currency === "USD"
    ? "USD"
    : "LD";
};

const getExchangeRate = (record) => {
  return toNumber(
    record?.exchange_rate ||
      record?.exchangeRate ||
      record?.usd_to_lrd_rate ||
      0
  );
};

const getOriginalPaymentAmount = (
  record
) => {
  if (
    record?.amount_paid !== undefined &&
    record?.amount_paid !== null
  ) {
    return toNumber(
      record.amount_paid
    );
  }

  if (
    record?.amount !== undefined &&
    record?.amount !== null
  ) {
    return toNumber(
      record.amount
    );
  }

  return 0;
};

const getNormalizedLrdAmount = (
  record,
  currency,
  exchangeRate
) => {
  if (
    record?.amount_paid_lrd !==
      undefined &&
    record?.amount_paid_lrd !== null
  ) {
    return toNumber(
      record.amount_paid_lrd
    );
  }

  const amount =
    getOriginalPaymentAmount(record);

  if (currency === "USD") {
    if (exchangeRate > 0) {
      return amount * exchangeRate;
    }

    return amount;
  }

  return amount;
};

const formatCurrency = (
  amount,
  currency
) => {
  return `${getCurrencyLabel(
    currency
  )} ${formatMoney(amount)}`;
};

const formatLrd = (amount) => {
  return `LD ${formatMoney(amount)}`;
};

const formatAllocation = (
  allocation,
  paymentCurrency,
  exchangeRate
) => {
  if (!allocation) {
    return "";
  }

  if (allocation.amount_display) {
    return String(
      allocation.amount_display
    );
  }

  const amount = toNumber(
    allocation.amount ||
      allocation.amount_paid ||
      allocation.value ||
      0
  );

  const currency = String(
    allocation.currency ||
      paymentCurrency ||
      "LRD"
  ).toUpperCase();

  if (currency === "USD") {
    return `USD ${formatMoney(
      amount
    )}`;
  }

  if (
    paymentCurrency === "USD" &&
    exchangeRate > 0
  ) {
    return `LD ${formatMoney(
      amount
    )}`;
  }

  return `LD ${formatMoney(
    amount
  )}`;
};

const getPaymentStatus = (
  balance,
  paidLrd
) => {
  const remaining =
    toNumber(balance);

  const paid =
    toNumber(paidLrd);

  if (remaining <= 0) {
    return {
      label: "PAID IN FULL",
      color: COLORS.green,
      background:
        COLORS.greenLight,
    };
  }

  if (paid > 0) {
    return {
      label: "PARTIAL PAYMENT",
      color: COLORS.goldDark,
      background:
        COLORS.goldLight,
    };
  }

  return {
    label: "BALANCE DUE",
    color: COLORS.red,
    background: "#FDECEC",
  };
};

/* =========================================================
   PDF STYLES
   IMPORTANT:
   TWO COPIES ARE DESIGNED TO FIT INSIDE ONE A4 PAGE.
========================================================= */

const styles = StyleSheet.create({
  page: {
    width: "100%",
    height: "100%",
    backgroundColor: COLORS.white,

    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 18,
    paddingRight: 18,

    fontFamily: "Helvetica",
    color: COLORS.black,
  },

  /*
   * Each copy is deliberately kept around
   * half the usable A4 page.
   */
  copy: {
    width: "100%",
    height: 395,

    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 5,

    backgroundColor:
      COLORS.white,

    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 8,
    paddingRight: 8,

    overflow: "hidden",
  },

  /* =====================================================
     HEADER
  ===================================================== */

  topBar: {
    height: 3,
    backgroundColor:
      COLORS.gold,
    marginBottom: 5,
  },

  header: {
    height: 49,
    flexDirection: "row",
    alignItems: "center",
  },

  logo: {
    width: 42,
    height: 42,
    objectFit: "contain",
    marginRight: 7,
  },

  brandArea: {
    flex: 1,
    paddingRight: 5,
  },

  brandName: {
    fontSize: 13,
    fontWeight: "bold",
    color: COLORS.greenDark,
    marginBottom: 2,
  },

  subtitle: {
    fontSize: 5.8,
    fontWeight: "bold",
    color: COLORS.black,
    marginBottom: 2,
  },

  officialText: {
    fontSize: 5.8,
    color: COLORS.muted,
  },

  receiptBadge: {
    width: 100,
    height: 39,

    backgroundColor:
      COLORS.greenDark,

    borderRadius: 4,

    alignItems: "center",
    justifyContent: "center",

    paddingHorizontal: 5,
  },

  receiptBadgeTitle: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.white,
  },

  receiptBadgeCopy: {
    fontSize: 6.3,
    fontWeight: "bold",
    color: COLORS.gold,
    marginTop: 2,
  },

  receiptBadgeNumber: {
    fontSize: 5.8,
    color: COLORS.white,
    marginTop: 2,
  },

  divider: {
    height: 1,
    backgroundColor:
      COLORS.border,
    marginTop: 4,
    marginBottom: 4,
  },

  /* =====================================================
     SECTION HEADER
  ===================================================== */

  sectionHeader: {
    height: 16,

    flexDirection: "row",
    alignItems: "center",

    backgroundColor:
      COLORS.green,

    borderRadius: 2,

    paddingLeft: 5,
    paddingRight: 5,

    marginBottom: 4,
  },

  goldMark: {
    width: 3,
    height: 9,

    backgroundColor:
      COLORS.gold,

    marginRight: 4,
  },

  sectionTitle: {
    fontSize: 6.3,
    fontWeight: "bold",
    color: COLORS.white,
  },

  /* =====================================================
     STUDENT
  ===================================================== */

  studentBox: {
    height: 62,

    flexDirection: "row",

    borderWidth: 1,
    borderColor:
      COLORS.border,

    backgroundColor:
      "#FAFCFB",

    borderRadius: 4,

    padding: 5,

    marginBottom: 4,
  },

  avatar: {
    width: 46,
    height: 46,

    borderRadius: 23,

    objectFit: "cover",

    marginRight: 7,
  },

  studentContent: {
    flex: 1,
  },

  studentName: {
    fontSize: 8.8,
    fontWeight: "bold",
    color: COLORS.black,

    marginBottom: 4,
  },

  studentGrid: {
    flexDirection: "row",
    width: "100%",
  },

  studentColumn: {
    width: "50%",
    paddingRight: 5,
  },

  fieldLabel: {
    fontSize: 5,
    color: COLORS.muted,
    marginBottom: 1,
  },

  fieldValue: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.black,
  },

  /* =====================================================
     PAYMENT DETAILS
  ===================================================== */

  paymentBox: {
    height: 67,

    flexDirection: "row",

    borderWidth: 1,
    borderColor:
      COLORS.border,

    borderRadius: 4,

    marginBottom: 4,
  },

  paymentDetails: {
    width: "78%",
    padding: 4,
  },

  paymentRow: {
    height: 9.5,

    flexDirection: "row",

    borderBottomWidth: 1,
    borderBottomColor:
      "#EDF0EE",
  },

  paymentLabel: {
    width: "32%",

    fontSize: 5.2,
    color: COLORS.muted,

    paddingTop: 1,
  },

  paymentValue: {
    width: "68%",

    fontSize: 5.7,
    fontWeight: "bold",
    color: COLORS.black,

    paddingTop: 1,
  },

  qrArea: {
    width: "22%",

    borderLeftWidth: 1,
    borderLeftColor:
      COLORS.border,

    alignItems: "center",
    justifyContent: "center",
  },

  qrImage: {
    width: 43,
    height: 43,
  },

  qrLabel: {
    fontSize: 4.8,
    fontWeight: "bold",
    color: COLORS.greenDark,
    marginTop: 1,
  },

  /* =====================================================
     ALLOCATION
  ===================================================== */

  allocationBox: {
    height: 37,

    borderWidth: 1,
    borderColor:
      COLORS.border,

    borderRadius: 4,

    padding: 4,

    marginBottom: 4,
  },

  allocationRow: {
    height: 13,

    flexDirection: "row",
    alignItems: "center",

    borderBottomWidth: 1,
    borderBottomColor:
      "#EDF0EE",
  },

  allocationName: {
    width: "78%",

    fontSize: 5.8,
    color: COLORS.black,
  },

  allocationAmount: {
    width: "22%",

    textAlign: "right",

    fontSize: 5.8,
    fontWeight: "bold",
    color: COLORS.black,
  },

  allocationEmpty: {
    fontSize: 5.8,
    color: COLORS.muted,
    paddingTop: 3,
  },

  /* =====================================================
     AMOUNTS
  ===================================================== */

  amounts: {
    height: 42,

    flexDirection: "row",

    marginBottom: 4,
  },

  amountCard: {
    width: "33.33%",

    height: 42,

    borderWidth: 1,
    borderColor:
      COLORS.border,

    borderRadius: 4,

    paddingTop: 5,
    paddingLeft: 5,
    paddingRight: 5,

    marginRight: 3,
  },

  amountCardLast: {
    marginRight: 0,
  },

  paidCard: {
    backgroundColor:
      COLORS.greenLight,

    borderColor:
      COLORS.green,
  },

  amountLabel: {
    fontSize: 5.2,
    color: COLORS.muted,

    marginBottom: 3,
  },

  amountValue: {
    fontSize: 7.7,
    fontWeight: "bold",
    color: COLORS.black,
  },

  /* =====================================================
     STATUS
  ===================================================== */

  statusBox: {
    height: 25,

    flexDirection: "row",
    alignItems: "center",
    justifyContent:
      "space-between",

    borderWidth: 1,
    borderColor:
      COLORS.border,

    borderRadius: 4,

    paddingLeft: 6,
    paddingRight: 6,

    marginBottom: 4,
  },

  statusLabel: {
    fontSize: 5.5,
    color: COLORS.muted,
  },

  statusBadge: {
    minWidth: 74,

    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 6,
    paddingRight: 6,

    borderRadius: 10,

    alignItems: "center",
  },

  statusText: {
    fontSize: 5.5,
    fontWeight: "bold",
  },

  /* =====================================================
     FOOTER
  ===================================================== */

  footer: {
    height: 27,

    flexDirection: "row",
    alignItems: "flex-end",

    borderTopWidth: 1,
    borderTopColor:
      COLORS.border,

    paddingTop: 4,
  },

  footerLeft: {
    width: "68%",
  },

  footerRight: {
    width: "32%",
    alignItems: "center",
  },

  footerText: {
    fontSize: 4.8,
    color: COLORS.muted,
    marginBottom: 1.5,
  },

  signatureLine: {
    width: "88%",

    borderBottomWidth: 1,
    borderBottomColor:
      COLORS.black,

    marginBottom: 2,
  },

  signatureText: {
    fontSize: 4.8,
    fontWeight: "bold",
    color: COLORS.black,
    textAlign: "center",
  },

  /* =====================================================
     CUT LINE BETWEEN COPIES
  ===================================================== */

  cutArea: {
    height: 12,

    flexDirection: "row",
    alignItems: "center",

    justifyContent:
      "center",
  },

  cutLine: {
    width: "94%",

    borderBottomWidth: 1,
    borderBottomColor:
      "#AEB7B2",

    borderBottomStyle:
      "dashed",
  },

  cutText: {
    position: "absolute",

    backgroundColor:
      COLORS.white,

    paddingLeft: 5,
    paddingRight: 5,

    fontSize: 4.5,
    color: COLORS.muted,
  },
});

/* =========================================================
   PDF RECEIPT COPY
========================================================= */

const ReceiptCopy = ({
  receiptData,
  qrCodeUrl,
  copyType,
}) => {
  const status =
    getPaymentStatus(
      receiptData.balance_value,
      receiptData.amount_paid_lrd_value
    );

  const allocations =
    Array.isArray(
      receiptData.allocations
    )
      ? receiptData.allocations
      : [];

  return (
    <View
      style={styles.copy}
      wrap={false}
    >
      {/* GOLD TOP BAR */}
      <View style={styles.topBar} />

      {/* HEADER */}
      <View style={styles.header}>
        <Image
          src={SchoolLogo}
          style={styles.logo}
        />

        <View style={styles.brandArea}>
          <Text style={styles.brandName}>
            {CENTER_NAME}
          </Text>

          <Text style={styles.subtitle}>
            {CENTER_SUBTITLE}
          </Text>

          <Text
            style={styles.officialText}
          >
            Official Training Payment
            Receipt
          </Text>
        </View>

        <View
          style={styles.receiptBadge}
        >
          <Text
            style={
              styles.receiptBadgeTitle
            }
          >
            PAYMENT RECEIPT
          </Text>

          <Text
            style={
              styles.receiptBadgeCopy
            }
          >
            {copyType}
          </Text>

          <Text
            style={
              styles.receiptBadgeNumber
            }
          >
            {receiptData.receipt_number}
          </Text>
        </View>
      </View>

      <View style={styles.divider} />

      {/* STUDENT SECTION */}
      <View
        style={styles.sectionHeader}
      >
        <View
          style={styles.goldMark}
        />

        <Text
          style={styles.sectionTitle}
        >
          TRAINEE / STUDENT INFORMATION
        </Text>
      </View>

      <View
        style={styles.studentBox}
      >
        <Image
          src={
            receiptData.profile_image ||
            AvatarStudent
          }
          style={styles.avatar}
        />

        <View
          style={styles.studentContent}
        >
          <Text
            style={styles.studentName}
          >
            {receiptData.student_name}
          </Text>

          <View
            style={styles.studentGrid}
          >
            <View
              style={styles.studentColumn}
            >
              <Text
                style={styles.fieldLabel}
              >
                PROGRAM / CLASS
              </Text>

              <Text
                style={styles.fieldValue}
              >
                {receiptData.grade_class_name ||
                  "N/A"}
              </Text>
            </View>

            <View
              style={styles.studentColumn}
            >
              <Text
                style={styles.fieldLabel}
              >
                STUDENT TYPE
              </Text>

              <Text
                style={styles.fieldValue}
              >
                {receiptData.student_type ||
                  "N/A"}
              </Text>
            </View>
          </View>

          <View
            style={[
              styles.studentGrid,
              {
                marginTop: 4,
              },
            ]}
          >
            <View
              style={styles.studentColumn}
            >
              <Text
                style={styles.fieldLabel}
              >
                PROGRAM / YEARLY FEE
              </Text>

              <Text
                style={styles.fieldValue}
              >
                {receiptData.yearly_fee}
              </Text>
            </View>

            <View
              style={styles.studentColumn}
            >
              <Text
                style={styles.fieldLabel}
              >
                OVERALL BALANCE
              </Text>

              <Text
                style={styles.fieldValue}
              >
                {receiptData.balance_fee}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* PAYMENT DETAILS */}
      <View
        style={styles.sectionHeader}
      >
        <View
          style={styles.goldMark}
        />

        <Text
          style={styles.sectionTitle}
        >
          PAYMENT DETAILS
        </Text>
      </View>

      <View
        style={styles.paymentBox}
      >
        <View
          style={styles.paymentDetails}
        >
          <View
            style={styles.paymentRow}
          >
            <Text
              style={styles.paymentLabel}
            >
              RECEIPT NUMBER
            </Text>

            <Text
              style={styles.paymentValue}
            >
              {receiptData.receipt_number}
            </Text>
          </View>

          <View
            style={styles.paymentRow}
          >
            <Text
              style={styles.paymentLabel}
            >
              PAYER
            </Text>

            <Text
              style={styles.paymentValue}
            >
              {receiptData.payer_name}
            </Text>
          </View>

          <View
            style={styles.paymentRow}
          >
            <Text
              style={styles.paymentLabel}
            >
              CURRENCY
            </Text>

            <Text
              style={styles.paymentValue}
            >
              {receiptData.payment_currency}
            </Text>
          </View>

          <View
            style={styles.paymentRow}
          >
            <Text
              style={styles.paymentLabel}
            >
              PAYMENT METHOD
            </Text>

            <Text
              style={styles.paymentValue}
            >
              {receiptData.payment_method}
            </Text>
          </View>

          <View
            style={styles.paymentRow}
          >
            <Text
              style={styles.paymentLabel}
            >
              DATE
            </Text>

            <Text
              style={styles.paymentValue}
            >
              {receiptData.date}
            </Text>
          </View>

          {receiptData.payment_currency ===
            "USD" &&
            receiptData.exchange_rate >
              0 && (
              <View
                style={
                  styles.paymentRow
                }
              >
                <Text
                  style={
                    styles.paymentLabel
                  }
                >
                  EXCHANGE RATE
                </Text>

                <Text
                  style={
                    styles.paymentValue
                  }
                >
                  1 USD = LD{" "}
                  {formatMoney(
                    receiptData.exchange_rate
                  )}
                </Text>
              </View>
            )}
        </View>

        <View
          style={styles.qrArea}
        >
          {qrCodeUrl ? (
            <Image
              src={qrCodeUrl}
              style={styles.qrImage}
            />
          ) : (
            <View
              style={{
                width: 43,
                height: 43,
                borderWidth: 1,
                borderColor:
                  COLORS.border,
              }}
            />
          )}

          <Text
            style={styles.qrLabel}
          >
            SCAN TO VERIFY
          </Text>
        </View>
      </View>

      {/* ALLOCATION */}
      <View
        style={styles.sectionHeader}
      >
        <View
          style={styles.goldMark}
        />

        <Text
          style={styles.sectionTitle}
        >
          PAYMENT / FEE ALLOCATION
        </Text>
      </View>

      <View
        style={styles.allocationBox}
      >
        {allocations.length >
        0 ? (
          allocations
            .slice(0, 2)
            .map(
              (
                allocation,
                index
              ) => (
                <View
                  key={`pdf-allocation-${index}`}
                  style={
                    styles.allocationRow
                  }
                >
                  <Text
                    style={
                      styles.allocationName
                    }
                  >
                    {
                      allocation.label
                    }
                  </Text>

                  <Text
                    style={
                      styles.allocationAmount
                    }
                  >
                    {
                      allocation.amount
                    }
                  </Text>
                </View>
              )
            )
        ) : (
          <Text
            style={
              styles.allocationEmpty
            }
          >
            {receiptData.services ||
              "Training Fee Payment"}
          </Text>
        )}
      </View>

      {/* AMOUNTS */}
      <View
        style={styles.amounts}
      >
        <View
          style={styles.amountCard}
        >
          <Text
            style={styles.amountLabel}
          >
            AMOUNT DUE
          </Text>

          <Text
            style={styles.amountValue}
          >
            {receiptData.amount_due}
          </Text>
        </View>

        <View
          style={[
            styles.amountCard,
            styles.paidCard,
          ]}
        >
          <Text
            style={styles.amountLabel}
          >
            AMOUNT PAID
          </Text>

          <Text
            style={styles.amountValue}
          >
            {receiptData.amount_paid}
          </Text>
        </View>

        <View
          style={[
            styles.amountCard,
            styles.amountCardLast,
          ]}
        >
          <Text
            style={styles.amountLabel}
          >
            BALANCE
          </Text>

          <Text
            style={styles.amountValue}
          >
            {receiptData.balance}
          </Text>
        </View>
      </View>

      {/* STATUS */}
      <View
        style={styles.statusBox}
      >
        <Text
          style={styles.statusLabel}
        >
          PAYMENT STATUS
        </Text>

        <View
          style={[
            styles.statusBadge,
            {
              backgroundColor:
                status.background,
            },
          ]}
        >
          <Text
            style={[
              styles.statusText,
              {
                color:
                  status.color,
              },
            ]}
          >
            {status.label}
          </Text>
        </View>
      </View>

      {/* FOOTER */}
      <View
        style={styles.footer}
      >
        <View
          style={styles.footerLeft}
        >
          <Text
            style={styles.footerText}
          >
            {receiptData.date}
          </Text>

          <Text
            style={styles.footerText}
          >
            Official payment record —
            {CENTER_NAME}
          </Text>

          <Text
            style={styles.footerText}
          >
            Powered by Polu Integrated
            School Management System
          </Text>
        </View>

        <View
          style={styles.footerRight}
        >
          <View
            style={styles.signatureLine}
          />

          <Text
            style={styles.signatureText}
          >
            AUTHORIZED FINANCE OFFICER
          </Text>
        </View>
      </View>
    </View>
  );
};

/* =========================================================
   SINGLE A4 PDF
   BOTH COPIES ARE CHILDREN OF THE SAME PAGE
========================================================= */

const ReceiptPDF = ({
  receiptData,
  qrCodeUrl,
}) => {
  return (
    <Document
      title={`${CENTER_NAME} Payment Receipt`}
      author={CENTER_NAME}
      subject="Official Training Payment Receipt"
    >
      <Page
        size="A4"
        orientation="portrait"
        style={styles.page}
        wrap={false}
      >
        {/* ===============================================
            TOP HALF — STUDENT COPY
        =============================================== */}

        <ReceiptCopy
          receiptData={receiptData}
          qrCodeUrl={qrCodeUrl}
          copyType="STUDENT COPY"
        />

        {/* ===============================================
            CUT / SEPARATION LINE
        =============================================== */}

        <View
          style={styles.cutArea}
        >
          <View
            style={styles.cutLine}
          />

          <Text
            style={styles.cutText}
          >
            CUT / SEPARATE HERE
          </Text>
        </View>

        {/* ===============================================
            BOTTOM HALF — FINANCE COPY
        =============================================== */}

        <ReceiptCopy
          receiptData={receiptData}
          qrCodeUrl={qrCodeUrl}
          copyType="FINANCE COPY"
        />
      </Page>
    </Document>
  );
};

/* =========================================================
   PREVIEW STYLES
========================================================= */

const previewStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,

    backgroundColor:
      "rgba(4, 20, 12, 0.72)",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    padding: "18px",
    boxSizing: "border-box",
  },

  modal: {
    width: "100%",
    maxWidth: "980px",
    maxHeight: "96vh",

    overflowY: "auto",

    backgroundColor:
      "#F3F6F4",

    borderRadius: "14px",

    boxShadow:
      "0 24px 70px rgba(0,0,0,0.30)",
  },

  modalHeader: {
    position: "sticky",
    top: 0,
    zIndex: 5,

    display: "flex",
    alignItems: "center",
    justifyContent:
      "space-between",

    gap: "15px",

    padding: "15px 18px",

    backgroundColor:
      COLORS.greenDark,

    borderBottom:
      `4px solid ${COLORS.gold}`,
  },

  modalTitle: {
    margin: 0,

    color: COLORS.white,

    fontSize: "17px",
    fontWeight: 800,
  },

  modalSubtitle: {
    margin: "3px 0 0",

    color: "#DCEDE3",

    fontSize: "11px",
  },

  closeButton: {
    border:
      "1px solid rgba(255,255,255,0.35)",

    backgroundColor:
      "transparent",

    color: COLORS.white,

    width: "36px",
    height: "36px",

    borderRadius: "8px",

    cursor: "pointer",

    fontSize: "20px",
    lineHeight: 1,
  },

  content: {
    padding: "18px",
  },

  copy: {
    backgroundColor:
      COLORS.white,

    border:
      `1px solid ${COLORS.border}`,

    borderRadius: "9px",

    padding: "14px",

    marginBottom: "14px",

    boxShadow:
      "0 4px 18px rgba(0,0,0,0.06)",
  },

  topBar: {
    height: "5px",

    backgroundColor:
      COLORS.gold,

    borderRadius: "5px",

    marginBottom: "10px",
  },

  header: {
    display: "flex",
    alignItems: "center",

    gap: "12px",
  },

  logo: {
    width: "62px",
    height: "62px",

    objectFit: "contain",

    flexShrink: 0,
  },

  brand: {
    flex: 1,
    minWidth: 0,
  },

  brandName: {
    margin: 0,

    color: COLORS.greenDark,

    fontSize: "21px",
    fontWeight: 900,
  },

  subtitle: {
    margin: "3px 0",

    color: COLORS.black,

    fontSize: "9px",
    fontWeight: 800,
  },

  official: {
    margin: 0,

    color: COLORS.muted,

    fontSize: "10px",
  },

  receiptBadge: {
    backgroundColor:
      COLORS.greenDark,

    color: COLORS.white,

    borderRadius: "8px",

    padding: "9px 11px",

    textAlign: "center",

    minWidth: "130px",
  },

  receiptBadgeTitle: {
    fontSize: "10px",
    fontWeight: 900,
  },

  receiptBadgeCopy: {
    color: COLORS.gold,

    fontSize: "9px",
    fontWeight: 900,

    marginTop: "2px",
  },

  receiptBadgeNumber: {
    fontSize: "8px",

    marginTop: "3px",
  },

  divider: {
    height: "1px",

    backgroundColor:
      COLORS.border,

    margin: "10px 0",
  },

  section: {
    marginBottom: "10px",
  },

  sectionTitle: {
    display: "flex",
    alignItems: "center",

    gap: "6px",

    backgroundColor:
      COLORS.green,

    color: COLORS.white,

    padding: "6px 9px",

    borderRadius: "4px",

    fontSize: "10px",
    fontWeight: 900,

    marginBottom: "7px",
  },

  goldLine: {
    width: "4px",
    height: "13px",

    backgroundColor:
      COLORS.gold,

    borderRadius: "3px",
  },

  student: {
    display: "flex",
    alignItems: "center",

    gap: "10px",

    border:
      `1px solid ${COLORS.border}`,

    backgroundColor:
      "#FAFCFB",

    borderRadius: "7px",

    padding: "9px",
  },

  avatar: {
    width: "58px",
    height: "58px",

    borderRadius: "50%",

    objectFit: "cover",

    border:
      `2px solid ${COLORS.gold}`,
  },

  studentBody: {
    flex: 1,
    minWidth: 0,
  },

  studentName: {
    color: COLORS.black,

    fontSize: "14px",
    fontWeight: 900,

    marginBottom: "7px",
  },

  grid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(180px, 1fr))",

    gap: "7px",
  },

  infoBox: {
    border:
      `1px solid ${COLORS.border}`,

    backgroundColor:
      COLORS.white,

    borderRadius: "5px",

    padding: "6px 7px",
  },

  label: {
    color: COLORS.muted,

    fontSize: "8px",
    fontWeight: 700,

    marginBottom: "2px",
  },

  value: {
    color: COLORS.black,

    fontSize: "10px",
    fontWeight: 800,

    wordBreak: "break-word",
  },

  paymentLayout: {
    display: "grid",

    gridTemplateColumns:
      "minmax(0, 1fr) 105px",

    gap: "10px",
  },

  detailTable: {
    border:
      `1px solid ${COLORS.border}`,

    borderRadius: "6px",

    overflow: "hidden",
  },

  detailRow: {
    display: "grid",

    gridTemplateColumns:
      "150px minmax(0, 1fr)",

    borderBottom:
      "1px solid #EDF0EE",
  },

  detailLabel: {
    padding: "6px 8px",

    color: COLORS.muted,

    backgroundColor:
      "#F7F9F8",

    fontSize: "9px",
    fontWeight: 700,
  },

  detailValue: {
    padding: "6px 8px",

    color: COLORS.black,

    fontSize: "9px",
    fontWeight: 800,

    wordBreak: "break-word",
  },

  qrBox: {
    border:
      `1px solid ${COLORS.border}`,

    borderRadius: "7px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",
    justifyContent: "center",

    padding: "7px",
  },

  qr: {
    width: "78px",
    height: "78px",
  },

  qrText: {
    color: COLORS.greenDark,

    fontSize: "7px",
    fontWeight: 900,

    marginTop: "5px",

    textAlign: "center",
  },

  allocation: {
    border:
      `1px solid ${COLORS.border}`,

    borderRadius: "6px",

    overflow: "hidden",
  },

  allocationRow: {
    display: "flex",

    justifyContent:
      "space-between",

    gap: "10px",

    padding: "6px 8px",

    borderBottom:
      "1px solid #EDF0EE",
  },

  allocationName: {
    color: COLORS.black,

    fontSize: "9px",
    fontWeight: 600,
  },

  allocationAmount: {
    color: COLORS.black,

    fontSize: "9px",
    fontWeight: 900,

    whiteSpace: "nowrap",
  },

  amountGrid: {
    display: "grid",

    gridTemplateColumns:
      "repeat(3, minmax(0, 1fr))",

    gap: "7px",
  },

  amountBox: {
    border:
      `1px solid ${COLORS.border}`,

    borderRadius: "6px",

    padding: "8px",

    backgroundColor:
      COLORS.white,
  },

  paidBox: {
    borderColor:
      COLORS.green,

    backgroundColor:
      COLORS.greenLight,
  },

  amountLabel: {
    color: COLORS.muted,

    fontSize: "8px",
    fontWeight: 700,

    marginBottom: "3px",
  },

  amountValue: {
    color: COLORS.black,

    fontSize: "13px",
    fontWeight: 900,
  },

  status: {
    display: "flex",

    alignItems: "center",

    justifyContent:
      "space-between",

    gap: "10px",

    border:
      `1px solid ${COLORS.border}`,

    borderRadius: "6px",

    padding: "7px 9px",

    marginTop: "8px",
  },

  statusLabel: {
    color: COLORS.muted,

    fontSize: "9px",
    fontWeight: 700,
  },

  statusBadge: {
    borderRadius: "20px",

    padding: "5px 10px",

    fontSize: "8px",
    fontWeight: 900,
  },

  footer: {
    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "flex-end",

    gap: "15px",

    borderTop:
      `1px solid ${COLORS.border}`,

    marginTop: "9px",

    paddingTop: "8px",
  },

  footerText: {
    color: COLORS.muted,

    fontSize: "8px",

    lineHeight: 1.45,
  },

  signature: {
    width: "180px",

    textAlign: "center",

    color: COLORS.black,

    fontSize: "8px",
    fontWeight: 800,
  },

  signatureLine: {
    borderBottom:
      "1px solid #222",

    marginBottom: "3px",
  },

  cutLine: {
    borderBottom:
      "1px dashed #AEB8B2",

    marginTop: "15px",
  },

  actions: {
    display: "flex",

    justifyContent:
      "flex-end",

    gap: "9px",

    flexWrap: "wrap",

    marginTop: "4px",
  },

  button: {
    border: "none",

    borderRadius: "7px",

    padding: "10px 17px",

    fontSize: "11px",
    fontWeight: 800,

    cursor: "pointer",
  },

  downloadButton: {
    backgroundColor:
      COLORS.green,

    color: COLORS.white,
  },

  closeAction: {
    backgroundColor:
      "#E7EBE9",

    color: COLORS.black,
  },
};

/* =========================================================
   HTML PREVIEW COPY
========================================================= */

const PreviewReceiptCopy = ({
  receiptData,
  qrCodeUrl,
  copyType,
  isLast,
}) => {
  const status =
    getPaymentStatus(
      receiptData.balance_value,
      receiptData.amount_paid_lrd_value
    );

  const allocations =
    receiptData.allocations || [];

  return (
    <div
      style={previewStyles.copy}
    >
      <div
        style={previewStyles.topBar}
      />

      <div
        style={previewStyles.header}
      >
        <img
          src={SchoolLogo}
          alt={CENTER_NAME}
          style={previewStyles.logo}
        />

        <div
          style={previewStyles.brand}
        >
          <h2
            style={
              previewStyles.brandName
            }
          >
            {CENTER_NAME}
          </h2>

          <div
            style={
              previewStyles.subtitle
            }
          >
            {CENTER_SUBTITLE}
          </div>

          <p
            style={
              previewStyles.official
            }
          >
            Official Training Payment
            Receipt
          </p>
        </div>

        <div
          style={
            previewStyles.receiptBadge
          }
        >
          <div
            style={
              previewStyles.receiptBadgeTitle
            }
          >
            PAYMENT RECEIPT
          </div>

          <div
            style={
              previewStyles.receiptBadgeCopy
            }
          >
            {copyType}
          </div>

          <div
            style={
              previewStyles.receiptBadgeNumber
            }
          >
            {receiptData.receipt_number}
          </div>
        </div>
      </div>

      <div
        style={previewStyles.divider}
      />

      <div
        style={previewStyles.section}
      >
        <div
          style={
            previewStyles.sectionTitle
          }
        >
          <span
            style={
              previewStyles.goldLine
            }
          />

          TRAINEE / STUDENT
          INFORMATION
        </div>

        <div
          style={previewStyles.student}
        >
          <img
            src={
              receiptData.profile_image ||
              AvatarStudent
            }
            alt="Student"
            style={previewStyles.avatar}
          />

          <div
            style={
              previewStyles.studentBody
            }
          >
            <div
              style={
                previewStyles.studentName
              }
            >
              {receiptData.student_name}
            </div>

            <div
              style={previewStyles.grid}
            >
              <div
                style={
                  previewStyles.infoBox
                }
              >
                <div
                  style={
                    previewStyles.label
                  }
                >
                  PROGRAM / CLASS
                </div>

                <div
                  style={
                    previewStyles.value
                  }
                >
                  {receiptData.grade_class_name ||
                    "N/A"}
                </div>
              </div>

              <div
                style={
                  previewStyles.infoBox
                }
              >
                <div
                  style={
                    previewStyles.label
                  }
                >
                  STUDENT TYPE
                </div>

                <div
                  style={
                    previewStyles.value
                  }
                >
                  {receiptData.student_type ||
                    "N/A"}
                </div>
              </div>

              <div
                style={
                  previewStyles.infoBox
                }
              >
                <div
                  style={
                    previewStyles.label
                  }
                >
                  PROGRAM FEE
                </div>

                <div
                  style={
                    previewStyles.value
                  }
                >
                  {receiptData.yearly_fee}
                </div>
              </div>

              <div
                style={
                  previewStyles.infoBox
                }
              >
                <div
                  style={
                    previewStyles.label
                  }
                >
                  OUTSTANDING BALANCE
                </div>

                <div
                  style={
                    previewStyles.value
                  }
                >
                  {receiptData.balance_fee}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={previewStyles.section}
      >
        <div
          style={
            previewStyles.sectionTitle
          }
        >
          <span
            style={
              previewStyles.goldLine
            }
          />

          PAYMENT DETAILS
        </div>

        <div
          style={
            previewStyles.paymentLayout
          }
        >
          <div
            style={
              previewStyles.detailTable
            }
          >
            <div
              style={
                previewStyles.detailRow
              }
            >
              <div
                style={
                  previewStyles.detailLabel
                }
              >
                RECEIPT NUMBER
              </div>

              <div
                style={
                  previewStyles.detailValue
                }
              >
                {receiptData.receipt_number}
              </div>
            </div>

            <div
              style={
                previewStyles.detailRow
              }
            >
              <div
                style={
                  previewStyles.detailLabel
                }
              >
                PAYER
              </div>

              <div
                style={
                  previewStyles.detailValue
                }
              >
                {receiptData.payer_name}
              </div>
            </div>

            <div
              style={
                previewStyles.detailRow
              }
            >
              <div
                style={
                  previewStyles.detailLabel
                }
              >
                CURRENCY
              </div>

              <div
                style={
                  previewStyles.detailValue
                }
              >
                {receiptData.payment_currency}
              </div>
            </div>

            <div
              style={
                previewStyles.detailRow
              }
            >
              <div
                style={
                  previewStyles.detailLabel
                }
              >
                PAYMENT METHOD
              </div>

              <div
                style={
                  previewStyles.detailValue
                }
              >
                {receiptData.payment_method}
              </div>
            </div>

            <div
              style={
                previewStyles.detailRow
              }
            >
              <div
                style={
                  previewStyles.detailLabel
                }
              >
                DATE
              </div>

              <div
                style={
                  previewStyles.detailValue
                }
              >
                {receiptData.date}
              </div>
            </div>

            {receiptData.payment_currency ===
              "USD" &&
              receiptData.exchange_rate >
                0 && (
                <div
                  style={
                    previewStyles.detailRow
                  }
                >
                  <div
                    style={
                      previewStyles.detailLabel
                    }
                  >
                    EXCHANGE RATE
                  </div>

                  <div
                    style={
                      previewStyles.detailValue
                    }
                  >
                    1 USD = LD{" "}
                    {formatMoney(
                      receiptData.exchange_rate
                    )}
                  </div>
                </div>
              )}
          </div>

          <div
            style={previewStyles.qrBox}
          >
            {qrCodeUrl && (
              <img
                src={qrCodeUrl}
                alt="Receipt QR"
                style={previewStyles.qr}
              />
            )}

            <div
              style={previewStyles.qrText}
            >
              SCAN TO VERIFY
            </div>
          </div>
        </div>
      </div>

      <div
        style={previewStyles.section}
      >
        <div
          style={
            previewStyles.sectionTitle
          }
        >
          <span
            style={
              previewStyles.goldLine
            }
          />

          PAYMENT / FEE ALLOCATION
        </div>

        <div
          style={
            previewStyles.allocation
          }
        >
          {allocations.length >
          0 ? (
            allocations.map(
              (
                allocation,
                index
              ) => (
                <div
                  key={`preview-allocation-${index}`}
                  style={
                    previewStyles.allocationRow
                  }
                >
                  <span
                    style={
                      previewStyles.allocationName
                    }
                  >
                    {
                      allocation.label
                    }
                  </span>

                  <span
                    style={
                      previewStyles.allocationAmount
                    }
                  >
                    {
                      allocation.amount
                    }
                  </span>
                </div>
              )
            )
          ) : (
            <div
              style={{
                padding: "8px",
                color:
                  COLORS.muted,
                fontSize: "9px",
              }}
            >
              {receiptData.services ||
                "Training fee payment"}
            </div>
          )}
        </div>
      </div>

      <div
        style={previewStyles.amountGrid}
      >
        <div
          style={
            previewStyles.amountBox
          }
        >
          <div
            style={
              previewStyles.amountLabel
            }
          >
            AMOUNT DUE
          </div>

          <div
            style={
              previewStyles.amountValue
            }
          >
            {receiptData.amount_due}
          </div>
        </div>

        <div
          style={{
            ...previewStyles.amountBox,
            ...previewStyles.paidBox,
          }}
        >
          <div
            style={
              previewStyles.amountLabel
            }
          >
            AMOUNT PAID
          </div>

          <div
            style={
              previewStyles.amountValue
            }
          >
            {receiptData.amount_paid}
          </div>
        </div>

        <div
          style={
            previewStyles.amountBox
          }
        >
          <div
            style={
              previewStyles.amountLabel
            }
          >
            REMAINING BALANCE
          </div>

          <div
            style={
              previewStyles.amountValue
            }
          >
            {receiptData.balance}
          </div>
        </div>
      </div>

      <div
        style={previewStyles.status}
      >
        <span
          style={
            previewStyles.statusLabel
          }
        >
          PAYMENT STATUS
        </span>

        <span
          style={{
            ...previewStyles.statusBadge,
            color: status.color,
            backgroundColor:
              status.background,
          }}
        >
          {status.label}
        </span>
      </div>

      <div
        style={previewStyles.footer}
      >
        <div>
          <div
            style={
              previewStyles.footerText
            }
          >
            {receiptData.date}
          </div>

          <div
            style={
              previewStyles.footerText
            }
          >
            Official payment record —
            {CENTER_NAME}
          </div>

          <div
            style={
              previewStyles.footerText
            }
          >
            Powered by Polu Integrated
            School Management System
          </div>
        </div>

        <div
          style={previewStyles.signature}
        >
          <div
            style={
              previewStyles.signatureLine
            }
          />

          AUTHORIZED FINANCE OFFICER
        </div>
      </div>

      {!isLast && (
        <div
          style={
            previewStyles.cutLine
          }
        />
      )}
    </div>
  );
};

/* =========================================================
   MAIN RECEIPT DIALOG
========================================================= */

const ReceiptDialog = ({
  open,
  onClose,
  studentDetails,
  currentFeeIndex,
}) => {
  const [
    receiptData,
    setReceiptData,
  ] = useState(null);

  const [
    qrCodeUrl,
    setQrCodeUrl,
  ] = useState("");

  useEffect(() => {
    if (
      !open ||
      !studentDetails
    ) {
      return;
    }

    const fee =
      studentDetails?.fee_records?.[
        currentFeeIndex
      ];

    if (!fee) {
      setReceiptData(null);
      setQrCodeUrl("");
      return;
    }

    const paymentCurrency =
      getPaymentCurrency(fee);

    const exchangeRate =
      getExchangeRate(fee);

    const originalPaymentAmount =
      getOriginalPaymentAmount(
        fee
      );

    const normalizedLrdAmount =
      getNormalizedLrdAmount(
        fee,
        paymentCurrency,
        exchangeRate
      );

    const balanceValue =
      toNumber(
        fee?.balance
      );

    const allocations =
      Array.isArray(
        fee?.allocations
      )
        ? fee.allocations
            .map(
              (allocation) => {
                const label =
                  allocation?.label ||
                  allocation?.name ||
                  allocation?.service ||
                  allocation?.description ||
                  "Payment";

                return {
                  label,
                  amount:
                    formatAllocation(
                      allocation,
                      paymentCurrency,
                      exchangeRate
                    ),
                };
              }
            )
            .filter(
              (item) =>
                item.label &&
                item.amount
            )
        : [];

    const paymentMethod =
      fee?.payment_method ||
      fee?.paymentMethod ||
      fee?.method ||
      "N/A";

    const receiptNumber =
      `REC-${Date.now()}`;

    const dateValue =
      fee?.created_at ||
      fee?.date_paid ||
      fee?.payment_date ||
      Date.now();

    const date =
      new Date(
        dateValue
      ).toLocaleString(
        "en-US",
        {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }
      );

    const data = {
      last_installment:
        fee?.last_installment ||
        fee?.installment ||
        "Training Fee",

      student_name:
        studentDetails?.student_name ||
        studentDetails?.name ||
        "N/A",

      grade_class_name:
        studentDetails?.grade_class_name ||
        studentDetails?.grade_class ||
        studentDetails?.class_name ||
        "N/A",

      student_type:
        studentDetails?.student_type ||
        "N/A",

      profile_image:
        studentDetails?.profile_image ||
        studentDetails?.profileImage ||
        AvatarStudent,

      yearly_fee:
        formatLrd(
          studentDetails?.yearly_school_fee ||
            fee?.yearly_school_fee ||
            0
        ),

      balance_fee:
        formatLrd(
          studentDetails?.overall_balance ||
            fee?.overall_balance ||
            0
        ),

      receipt_number:
        receiptNumber,

      payer_name:
        fee?.payer_name ||
        fee?.payer ||
        studentDetails?.student_name ||
        studentDetails?.name ||
        "N/A",

      payment_currency:
        paymentCurrency,

      exchange_rate:
        exchangeRate,

      amount_paid_original:
        originalPaymentAmount,

      amount_paid_lrd_value:
        normalizedLrdAmount,

      services:
        fee?.services ||
        fee?.service ||
        fee?.description ||
        fee?.last_installment ||
        "Training Fee",

      allocations,

      payment_method:
        paymentMethod,

      amount_due:
        formatLrd(
          fee?.fee_charge ||
            fee?.amount_due ||
            0
        ),

      amount_paid:
        formatCurrency(
          originalPaymentAmount,
          paymentCurrency
        ),

      amount_paid_lrd:
        formatLrd(
          normalizedLrdAmount
        ),

      balance:
        formatLrd(
          balanceValue
        ),

      balance_value:
        balanceValue,

      date,
    };

    setReceiptData(data);
    setQrCodeUrl("");

    let cancelled = false;

    const qrPayload =
      JSON.stringify({
        receipt:
          receiptNumber,

        student:
          data.student_name,

        payer:
          data.payer_name,

        currency:
          paymentCurrency,

        amount:
          originalPaymentAmount,

        amount_lrd:
          normalizedLrdAmount,

        balance:
          balanceValue,

        date,
      });

    QRCode.toDataURL(
      qrPayload,
      {
        width: 180,
        margin: 1,
        errorCorrectionLevel:
          "M",
      }
    )
      .then((url) => {
        if (!cancelled) {
          setQrCodeUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQrCodeUrl("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    studentDetails,
    currentFeeIndex,
  ]);

  if (!open) {
    return null;
  }

  if (!receiptData) {
    return null;
  }

  return (
    <div
      style={
        previewStyles.overlay
      }
      onMouseDown={(event) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose();
        }
      }}
    >
      <div
        style={
          previewStyles.modal
        }
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        {/* HEADER */}
        <div
          style={
            previewStyles.modalHeader
          }
        >
          <div>
            <h3
              style={
                previewStyles.modalTitle
              }
            >
              {CENTER_NAME} —
              Payment Receipt
            </h3>

            <p
              style={
                previewStyles.modalSubtitle
              }
            >
              Student Copy + Finance
              Copy on one A4 PDF page
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={
              previewStyles.closeButton
            }
          >
            ×
          </button>
        </div>

        {/* PREVIEW */}
        <div
          style={
            previewStyles.content
          }
        >
          <PreviewReceiptCopy
            receiptData={
              receiptData
            }
            qrCodeUrl={
              qrCodeUrl
            }
            copyType="STUDENT COPY"
            isLast={false}
          />

          <PreviewReceiptCopy
            receiptData={
              receiptData
            }
            qrCodeUrl={
              qrCodeUrl
            }
            copyType="FINANCE COPY"
            isLast={true}
          />

          {/* ACTIONS */}
          <div
            style={
              previewStyles.actions
            }
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                ...previewStyles.button,
                ...previewStyles.closeAction,
              }}
            >
              Close
            </button>

            <PDFDownloadLink
              document={
                <ReceiptPDF
                  receiptData={
                    receiptData
                  }
                  qrCodeUrl={
                    qrCodeUrl
                  }
                />
              }
              fileName={`${CENTER_NAME.replace(
                /\s+/g,
                "_"
              )}_Receipt_${
                receiptData.receipt_number
              }.pdf`}
              style={{
                ...previewStyles.button,
                ...previewStyles.downloadButton,
                textDecoration:
                  "none",

                display:
                  "inline-flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",
              }}
            >
              {({
                loading,
              }) =>
                loading
                  ? "Preparing PDF..."
                  : "Download One-Page PDF"
              }
            </PDFDownloadLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptDialog;